import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import withSizes from 'react-sizes';
import VisibilitySensor from 'react-visibility-sensor';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import {
  getSavedAssessments,
  getTakenAssessments,
  saveAssessment,
} from 'containers/Assessments/actions';
import {
  makeSelectSavedAssessments,
  makeSelectTakenAssessments,
} from 'containers/Assessments/selectors';
import {
  isAssessmentSaved,
  isAssessmentTaken,
} from 'containers/Assessments/utils';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { trackFavoriteAction } from 'containers/Favorites/utils';
import AssessmentsItem from 'containers/Assessments/AssessmentsItem';
import PeopleItem from 'containers/People/PeopleItem';
import Carousel from 'components/ResourcesCarousel';
import ResourceItem from 'components/ResourceItem';
import { getFormattedType } from 'components/ResourceItemSelector/utils';
import Mixpanel from 'utils/mixpanelService';

const useStyles = makeStyles(theme => ({
  root: {
    [theme.breakpoints.down('480')]: {
      position: 'relative',
    },
  },
  viewMore: {
    [theme.breakpoints.down('480')]: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      textAlign: 'center',
      margin: 0,
      padding: '150px 0 40px 0 !important',
      backgroundImage:
        'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.74), rgba(255,255,255,1))',
    },
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  carouselContainer: {
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    position: 'relative',
  },
  viewAllContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  viewAllButton: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  viewAllText: {
    alignItems: 'center',
    ...theme.typography.overline,
  },
  viewAllIcon: {
    marginLeft: 10,
    marginBottom: 2,
  },
  itemsWrapper: {
    '& > div': {
      maxWidth: '100%',
    },
  },
}));

const stateSelector = createStructuredSelector({
  auth: makeSelectAuth(),
  savedAssessments: makeSelectSavedAssessments(),
  takenAssessments: makeSelectTakenAssessments(),
});

const ItemPageResourcesRow = ({
  col,
  data,
  header,
  isMobile,
  isTablet,
  pageData,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { auth, savedAssessments, takenAssessments } = useSelector(
    stateSelector,
  );

  useEffect(() => {
    dispatch(getSavedAssessments());
    dispatch(getTakenAssessments());
  }, []);

  const handleSaveAssessment = (
    assessmentID,
    slug,
    name,
    time,
    callToAction,
    saved,
  ) => {
    let nodeID;
    if (saved)
      [{ nodeID }] = savedAssessments.filter(el => el.sys.id === assessmentID);

    const payload = {
      nodeID,
      assessment: {
        assessmentID,
        slug,
        name,
        callToAction,
        time,
      },
      save: !saved,
    };

    trackFavoriteAction(
      'Card Click',
      {
        page: 'Home Page',
        type: 'Assessments',
        resourceID: assessmentID,
        slug,
        name,
      },
      auth,
      saved,
    );

    dispatch(saveAssessment(payload));
  };

  const onVisibilityChange = isVisible => {
    if (_isEmpty(pageData)) return null;
    const {
      fields,
      fields: { blog, name, title },
      sys,
    } = pageData[0];
    let type = getFormattedType(sys.contentType.sys.id);
    if (blog) type = 'blog';
    else if (_get(fields, 'type') === 'News') type = 'news';
    if (isVisible) {
      Mixpanel.track('Resource Page - ScrollTo', {
        path: window.location.pathname,
        section: 'other-resources-panel',
        type,
        name: name || title,
      });
    }
    return null;
  };

  return useMemo(() => {
    const renderResourceItem = item => {
      switch (item.type) {
        case 'Assessments': {
          const saved = isAssessmentSaved(savedAssessments, item);
          const taken = isAssessmentTaken(takenAssessments, item);
          return (
            <div className={classes.itemsWrapper}>
              <AssessmentsItem
                recommendation
                key={item.slug}
                item={{ fields: item, sys: { id: item.objectID } }}
                saved={saved}
                taken={taken}
                save={handleSaveAssessment}
              />
            </div>
          );
        }
        case 'Videos':
          return (
            <ResourceItem
              recommendation
              key={item.slug}
              item={item}
              type="video"
              route="/videos"
            />
          );
        case 'App':
        case 'Apps':
          return (
            <ResourceItem
              recommendation
              key={item.slug}
              item={item}
              type="app"
              route="/apps"
            />
          );
        case 'Podcasts':
          return (
            <ResourceItem
              recommendation
              key={item.slug}
              item={item}
              type="podcast"
              route="/podcasts"
            />
          );
        case 'People':
          return <PeopleItem recommendation key={item.slug} item={item} />;
        case 'Books':
          return (
            <ResourceItem
              recommendation
              key={item.slug}
              item={item}
              type="book"
              route="/books"
            />
          );
        case 'Articles':
        case 'News':
          return (
            <ResourceItem
              recommendation
              key={item.slug}
              item={item}
              type="article"
              route="/articles"
            />
          );
        case 'Organizations':
          return (
            <ResourceItem
              recommendation
              key={item.slug}
              item={item}
              type="organization"
              route="/organizations"
            />
          );
        case 'Events':
          return (
            <ResourceItem
              recommendation
              key={item.slug}
              item={item}
              type="event"
              route="/events"
            />
          );
        case 'FAQ':
          return (
            <ResourceItem
              recommendation
              key={item.slug}
              item={item}
              type="faq"
            />
          );
        case 'Online Programs':
          return (
            <ResourceItem
              key={item.slug}
              item={item}
              type="program"
              route="/programs"
            />
          );
        default:
          return null;
      }
    };

    if (_isEmpty(data)) return null;

    return (
      <Grid container spacing={2} className={classes.root}>
        <Grid item xs={12}>
          <VisibilitySensor
            onChange={onVisibilityChange}
            scrollCheck
            scrollDelay={500}
          >
            <Grid container direction="row" justify="space-between">
              <Grid item xs={8} className={classes.headerContainer}>
                <Typography variant="h5" color="textPrimary">
                  {header}
                </Typography>
              </Grid>
            </Grid>
          </VisibilitySensor>
        </Grid>
        <Grid item xs={12} className={classes.carouselContainer}>
          <Carousel
            bigArrow={isMobile || isTablet}
            slidesToSlide={isMobile ? 1 : isTablet ? 2 : col}
            swipeable
            responsive={{
              desktop: {
                breakpoint: {
                  max: 3000,
                  min: 1024,
                },
                items: col,
                partialVisibilityGutter: 40,
              },
              mobile: {
                breakpoint: {
                  max: 480,
                  min: 0,
                },
                items: 1,
                partialVisibilityGutter: 30,
              },
              tablet: {
                breakpoint: {
                  max: 1024,
                  min: 480,
                },
                items: 2,
                partialVisibilityGutter: 30,
              },
            }}
          >
            {data.map(item => renderResourceItem(item))}
          </Carousel>
        </Grid>
      </Grid>
    );
  }, [data]);
};

ItemPageResourcesRow.propTypes = {
  col: PropTypes.number,
  data: PropTypes.array,
  header: PropTypes.string,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  pageData: PropTypes.object,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 480,
  isTablet: width <= 1024,
});

export default withSizes(mapSizesToProps)(ItemPageResourcesRow);
